// src/pages/finance/FinancePage.jsx
import React, { useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { List, Home, BarChart2, CreditCard } from 'lucide-react';

const FinancePage = () => {
  const location = useLocation();
  
  // Determine active tab
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/finance/dashboard')) return 'dashboard';
    if (path === '/finance' || path === '/finance/' || path.includes('/finance/transaction')) return 'transactions';
    if (path.includes('/finance/accounts')) return 'accounts';
    if (path.includes('/finance/reports')) return 'reports';
    return 'transactions'; // Default to transactions
  };
  
  const activeTab = getActiveTab();
  
  // Scroll to active tab on mount and when tab changes
  useEffect(() => {
    const activeTabElement = document.querySelector('.active-tab');
    if (activeTabElement) {
      activeTabElement.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeTab]);

  return (
    <div className="space-y-6 overflow-hidden">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h1 className="text-xl font-semibold text-gray-900">Finance</h1>
        <p className="text-sm text-gray-500 mt-1">
          Track cash flow, profitability, receivables, payables, and key reports for your business.
        </p>
      </div>

      {/* Tabs */}
      <div className="overflow-x-auto -mx-4 border-b border-gray-200">
        <nav className="flex min-w-max px-6">
            <Link
              to="/finance/dashboard"
              className={`py-4 px-4 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
                activeTab === 'dashboard'
                  ? 'border-[#190192] text-[#190192] active-tab' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center">
                <Home size={16} className={`mr-2 ${activeTab === 'dashboard' ? 'text-[#190192]' : 'text-gray-400'}`} />
                Dashboard
              </span>
            </Link>
            
            <Link
              to="/finance/transactions"
              className={`py-4 px-4 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
                activeTab === 'transactions'
                  ? 'border-[#190192] text-[#190192] active-tab' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center">
                <List size={16} className={`mr-2 ${activeTab === 'transactions' ? 'text-[#190192]' : 'text-gray-400'}`} />
                Transactions
              </span>
            </Link>
            
            <Link
              to="/finance/accounts"
              className={`py-4 px-4 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
                activeTab === 'accounts'
                  ? 'border-[#190192] text-[#190192] active-tab' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center">
                <CreditCard size={16} className={`mr-2 ${activeTab === 'accounts' ? 'text-[#190192]' : 'text-gray-400'}`} />
                Accounts
              </span>
            </Link>
            
            <Link
              to="/finance/reports"
              className={`py-4 px-4 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
                activeTab === 'reports'
                  ? 'border-[#190192] text-[#190192] active-tab' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center">
                <BarChart2 size={16} className={`mr-2 ${activeTab === 'reports' ? 'text-[#190192]' : 'text-gray-400'}`} />
                Reports
              </span>
            </Link>
        </nav>
      </div>

      {/* Nested Routes */}
      <Outlet />
    </div>
  );
};

export default FinancePage;
